import {
  InlineContent,
  PartialInlineContent,
  StyledText,
} from '@packages/blocknote-core'
import styles from '@packages/blocknote-core/extensions/Blocks/nodes/Block.module.css'

type BlockContentProps = {
  content: string | InlineContent[] | PartialInlineContent[]
  keyword?: string
}

export function BlockContent({ content, keyword }: BlockContentProps) {
  if (typeof content === 'string') {
    return (
      <span className={`${styles.inlineContent} wrap-anywhere text-gray-900 dark:text-gray-100`}>
        {highlightText(content, keyword)}
      </span>
    )
  }
  return <BlockInlineContent content={content} keyword={keyword} />
}

function BlockInlineContent({
  content,
  keyword,
}: {
  content: PartialInlineContent[] | InlineContent[]
  keyword?: string
}) {
  return (
    <div className={`${styles.inlineContent} wrap-anywhere break-words pr-4 text-gray-900 dark:text-gray-100`}>
      {content.map((inlineContent, index) => {
        if (inlineContent.type === 'link') {
          return (
            <a
              href={inlineContent.href}
              key={index}
              className='text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
            >
              {typeof inlineContent.content === 'string'
                ? inlineContent.content
                : inlineContent.content.map((text, index) =>
                    renderStyledText(text, index)
                  )}
            </a>
          )
        }
        return renderStyledText(inlineContent, index, keyword)
      })}
    </div>
  )
}

function highlightText(text: string, keyword?: string) {
  if (!keyword) {
    return text
  }
  const regex = new RegExp(`(${keyword})`, 'gi')
  const parts = text.split(regex)
  return (
    <span>
      {parts.map((part, index) => {
        if (part.toLowerCase() === keyword.toLowerCase()) {
          return <mark key={index}>{part}</mark>
        }
        return part
      })}
    </span>
  )
}

function renderStyledText(
  content: StyledText,
  index: number,
  keyword?: string
) {
  const text = keyword ? highlightText(content.text, keyword) : content.text
  if (content.styles.bold) {
    return <strong key={index}>{text}</strong>
  }
  if (content.styles.italic) {
    return <em key={index}>{text}</em>
  }
  if (content.styles.strikethrough) {
    return <del key={index}>{text}</del>
  }
  if (content.styles.underlined) {
    return <u key={index}>{text}</u>
  }
  if (content.styles.highlighted) {
    return <mark key={index}>{text}</mark>
  }
  if (content.styles.timeString) {
    return (
      <span data-content-type='time-string' key={index}>
        {text}
      </span>
    )
  }
  if (content.styles.code) {
    return <code key={index}>{text}</code>
  }
  if (content.styles.hashtag) {
    return (
      <a
        data-hashtag='hashtag'
        key={index}
        className='text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
      >
        {text}
      </a>
    )
  }
  if (content.styles.wikilink) {
    return (
      <a
        data-wikilink='wikilink'
        key={index}
        className='text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'
      >
        {text}
      </a>
    )
  }
  if (content.styles.datelink) {
    return (
      <a
        data-datelink='datelink'
        key={index}
        className='text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
      >
        {text}
      </a>
    )
  }

  return (
    <span
      className='styledText text-gray-900 dark:text-gray-100'
      data-type={content.type}
      key={index}
    >
      {text}
    </span>
  )
}
